// Raw GM list fragments. PHP supplies gate + SELECT rows; every cell and control is built here.
import { decodeHtmlEntitiesForPlainText as plain } from '../shared/decode-html-entities';
import { encounterZoneLabel } from '../janus-engine/compile/encounter-zone-label';

interface EncounterRow {
    encounter_id: string;
    encounter_name: string | null;
    encounter_private: string | number | null;
    encounter_explored?: string | number | null;
    encounter_zone_prefix: string | null;
    encounter_zone_number: string | number | null;
    location_zone_prefix: string | null;
    encounter_location_id: string | null;
    encounter_location_name: string | null;
    encounter_location_parent_id: string | null;
    encounter_location_parent_name: string | null;
    encounter_location_type?: string | null;
    waypoint_rows?: string | WaypointRow[] | null;
}
interface WaypointRow {
    inventory_id: string;
    inventory_name: string | null;
    item_name: string | null;
    item_type: string | null;
    waypoint_sort_key: string;
}
interface GmPanelData {
    schema: 'gm-encounter-panels-v1';
    encounters: EncounterRow[];
    readable_encounter_ids: string[];
    csrf_token: string;
    quick_filter: boolean;
    create_href: string | null;
    empty_message: string;
}
interface LinkRow extends EncounterRow {
    encounter_link_from: string;
    encounter_link_to: string;
    encounter_link_private: string | number | null;
}
interface LinkPanelData {
    schema: 'encounter-link-panels-v1';
    encounter_id: string;
    has_parent: boolean;
    links: LinkRow[];
    readable_encounter_ids: string[];
    readable_location_ids: string[];
    suppress_empty_peer_message: boolean;
    csrf_token: string;
}
interface PanelActions {
    openSideForm: (href: string) => void;
    gmPushAllToEncounter: (id: string) => Promise<void>;
    gmLoadCampaignEncounter: (id: string) => Promise<void>;
    gmPullAllCreaturesFromEncounter: (id: string) => Promise<void>;
    gmPushPartyToEncounter: (id: string, encounterId?: string) => Promise<void>;
    refreshEncounterLinksSummary?: () => Promise<void>;
}
const actions = window as unknown as PanelActions;
const url = (path: string): string => new URL(path, BASE_URL).href;
const key = (value: unknown): string => String(value ?? '').toUpperCase();
// PHP strcasecmp folds ASCII; do not let the browser locale change the peer sort order.
const compare = (a: unknown, b: unknown): number => {
    const fold = (value: unknown): string => String(value ?? '').replace(/[A-Z]/g, char => char.toLowerCase());
    const aa = fold(a), bb = fold(b);
    return aa < bb ? -1 : aa > bb ? 1 : 0;
};
const zone = (row: EncounterRow): string => plain(encounterZoneLabel({ encounters: row }));

function node<K extends keyof HTMLElementTagNameMap>(tag: K, className = '', text?: string): HTMLElementTagNameMap[K] {
    const element = document.createElement(tag);
    element.className = className;
    if (text !== undefined) element.textContent = text;
    return element;
}
function icon(parent: HTMLElement, id: string, size = 25): void {
    const span = node('span');
    span.setAttribute('aria-hidden', 'true');
    // Only the shared icon renderer's markup enters this sink. Stored names always use textContent.
    span.innerHTML = iconMap(id, size, size, false);
    parent.append(span);
}
function button(parent: HTMLElement, className: string, label: string, iconId: string | null,
    click: () => void | Promise<unknown>, id?: string): HTMLButtonElement {
    const element = node('button', className);
    element.type = 'button';
    element.title = label;
    element.setAttribute('aria-label', label);
    if (id) element.id = id;
    if (iconId) icon(element, iconId);
    else element.textContent = label;
    element.addEventListener('click', async () => {
        if (element.disabled) return;
        const children = [...element.childNodes];
        element.disabled = true;
        element.setAttribute('aria-busy', 'true');
        element.textContent = 'Working…';
        try { await click(); }
        finally {
            element.replaceChildren(...children);
            element.removeAttribute('aria-busy');
            element.disabled = false;
        }
    });
    parent.append(element);
    return element;
}
function link(parent: HTMLElement, href: string, className: string, label: string, iconId?: string): HTMLAnchorElement {
    const element = node('a', className);
    element.href = href;
    element.target = '_blank';
    element.rel = 'noopener';
    if (iconId) {
        element.title = label;
        element.setAttribute('aria-label', label);
        icon(element, iconId);
    } else element.textContent = label;
    parent.append(element);
    return element;
}
function cell(row: HTMLTableRowElement, className = '', text?: string): HTMLTableCellElement {
    const td = node('td', className, text);
    row.append(td);
    return td;
}
function postCopy(parent: HTMLElement, id: string, csrf: string): void {
    const form = node('form', 'd-inline m-0');
    form.method = 'post';
    form.action = url('options/tools/encounters/copy_encounter_inc.php');
    form.target = '_blank';
    for (const [name, value] of [['encounter_id', id], ['csrf_token', csrf]]) {
        const input = node('input');
        input.type = 'hidden';
        input.name = name!;
        input.value = value!;
        form.append(input);
    }
    const submit = node('button', 'btn btn-sm btn-info');
    submit.type = 'submit';
    submit.title = 'Copy encounter';
    submit.setAttribute('aria-label', 'Copy encounter');
    icon(submit, 'copy_encounter');
    form.append(submit);
    parent.append(form);
}
function gmControls(td: HTMLElement, row: EncounterRow, csrf: string): void {
    const id = row.encounter_id;
    const wrap = node('div', 'gm-encounter-list-actions d-flex align-items-center gap-1');
    td.append(wrap);
    button(wrap, 'btn btn-primary btn-sm', 'Move all creatures to this encounter', 'push_all_creatures_to_encounter',
        () => actions.gmPushAllToEncounter(id), `btn_push_all_creatures_to_encounter_${id}`);
    button(wrap, 'btn btn-primary btn-sm', 'Load this encounter', 'load_encounter',
        () => actions.gmLoadCampaignEncounter(id), `btn_load_encounter_${id}`);
    button(wrap, 'btn btn-secondary btn-sm', 'Pull all creatures from this encounter', 'pull_all_creatures_from_encounter',
        () => actions.gmPullAllCreaturesFromEncounter(id), `btn_pull_all_creatures_from_encounter_${id}`);
    const exploredWrap = node('div');
    exploredWrap.title = 'Toggle explored';
    wrap.append(exploredWrap);
    const setExplored = (value: '0' | '1'): void => {
        void queryData(url('options/tools/encounters/toggle_encounter_explored_inc.php'), `div_refresh_encounter_${id}`,
            { item_type: 'encounter', encounter_id: id, encounter_explored: value, csrf_token: csrf });
        unexplored.style.display = value === '0' ? 'block' : 'none';
        explored.style.display = value === '1' ? 'block' : 'none';
    };
    const unexplored = button(exploredWrap, 'btn btn-warning btn-sm', 'Mark explored', 'unexplored', () => setExplored('1'), `encounter_unexplored_${id}`);
    const explored = button(exploredWrap, 'btn btn-success btn-sm', 'Mark unexplored', 'explored', () => setExplored('0'), `encounter_explored_${id}`);
    unexplored.style.display = Number(row.encounter_explored ?? 0) === 0 ? 'block' : 'none';
    explored.style.display = Number(row.encounter_explored ?? 0) === 1 ? 'block' : 'none';
    link(wrap, url(`encounter.php?scene=edit&encounter_id=${encodeURIComponent(id)}`), 'btn btn-sm btn-info', 'Edit encounter', 'modify_encounter');
    postCopy(wrap, id, csrf);
    const response = node('div');
    response.id = `div_refresh_encounter_${id}`;
    wrap.append(response);
}
function waypoints(row: EncounterRow): WaypointRow[] {
    const rows = typeof row.waypoint_rows === 'string' ? JSON.parse(row.waypoint_rows) as WaypointRow[] : row.waypoint_rows ?? [];
    // MySQL collation weights preserve the previous ORDER BY waypoint_name for accents,
    // punctuation and case, regardless of the browser's locale. No label is formatted in PHP.
    return rows.filter(row => /^[\da-f]{8}(-[\da-f]{4}){3}-[\da-f]{12}$/i.test(row.inventory_id)).sort((a, b) => compare(a.waypoint_sort_key, b.waypoint_sort_key)
        || compare(a.inventory_id, b.inventory_id));
}
function renderGmPanel(output: HTMLElement, data: GmPanelData): void {
    const rows = [...data.encounters].sort((a, b) => {
        // Match the old stable sort_encounters_by_prefix_and_zone after the SELECT's tie order.
        const pa = a.location_zone_prefix ?? '', pb = b.location_zone_prefix ?? '';
        return (pa < pb ? -1 : pa > pb ? 1 : 0) || Number(a.encounter_zone_number ?? 0) - Number(b.encounter_zone_number ?? 0);
    });
    if (data.quick_filter) {
        const chrome = node('div', `d-flex flex-wrap align-items-center ${rows.length ? '' : 'justify-content-end'} gap-2 mb-2 gm-encounter-list-wrap`);
        if (rows.length) {
            const search = node('div', 'position-relative flex-grow-1');
            search.style.minWidth = '12rem';
            const label = node('label', 'visually-hidden', 'Filter encounter list');
            label.htmlFor = 'input_gm_encounter_list_filter';
            const input = node('input', 'form-control form-control-sm w-100 pe-4');
            input.type = 'search';
            input.id = label.htmlFor;
            input.placeholder = 'Name, zone, or location…';
            input.autocomplete = 'off';
            input.setAttribute('aria-label', 'Filter encounter list');
            const searchIcon = node('span', 'gm-encounter-list-search-icon position-absolute top-50 end-0 translate-middle-y me-2 user-select-none');
            searchIcon.style.pointerEvents = 'none';
            icon(searchIcon, 'search', 14);
            search.append(label, input, searchIcon);
            chrome.append(search);
        }
        if (data.create_href) button(chrome, 'btn btn-sm btn-primary d-inline-flex align-items-center justify-content-center flex-shrink-0',
            'Create encounter', 'create_encounter', () => actions.openSideForm(data.create_href!));
        output.append(chrome);
    }
    if (!rows.length) {
        const empty = node('div', 'gm-encounter-list-empty d-flex flex-column align-items-center justify-content-center gap-2 border rounded p-4 text-center text-muted');
        empty.dataset.gmEncounterListEmpty = '';
        empty.setAttribute('role', 'status');
        icon(empty, 'encounter', 28);
        empty.append(node('strong', '', data.empty_message));
        output.append(empty);
        return;
    }
    const responsive = node('div', 'table-responsive-xxl');
    const table = node('table', 'table table-striped table-hover table-sm gm-encounter-list-table');
    const head = node('thead'), headers = node('tr');
    for (const [iconId, label] of [['settings', 'Settings'], ['location_parent', 'Parent Location'], ['location', 'Location'], ['zone', 'Zone'], ['encounter', 'Encounter']]) {
        const th = node('th', 'text-center');
        th.scope = 'col';
        th.setAttribute('aria-label', label!);
        icon(th, iconId!);
        headers.append(th);
    }
    head.append(headers);
    const body = node('tbody');
    table.append(head, body);
    responsive.append(table);
    output.append(responsive);
    for (const row of rows) {
        const tr = node('tr', 'gm-encounter-list-main-row');
        tr.dataset.gmEncounterId = row.encounter_id;
        const settings = cell(tr, 'gm-encounter-list-actions-cell text-nowrap');
        gmControls(settings, row, data.csrf_token);
        cell(tr, 'text-start', plain(row.encounter_location_parent_name));
        cell(tr, 'text-start', plain(row.encounter_location_name));
        const readable = data.readable_encounter_ids.some(id => key(id) === key(row.encounter_id));
        const zoneCell = cell(tr, 'text-end', readable ? zone(row) : 'Encounter display unavailable: read-access');
        if (!readable) zoneCell.classList.add('text-warning');
        link(cell(tr), url(`encounter.php?encounter_id=${encodeURIComponent(row.encounter_id)}`), 'link-primary', plain(row.encounter_name));
        const child = node('tr', 'tablesorter-childRow gm-encounter-list-travel-row');
        const travelCell = cell(child);
        travelCell.colSpan = 5;
        const arrivals = node('div', 'ft-encounter-arrival-strip d-flex align-items-center flex-wrap gap-1 p-2 border rounded');
        arrivals.dataset.ftEncounterArrivalPoints = row.encounter_id;
        arrivals.append(node('span', 'small fw-semibold me-1', 'Move party to:'));
        const points = waypoints(row);
        for (const point of points) {
            const label = plain((point.inventory_name || point.item_name || 'Waypoint').trim() || 'Waypoint');
            const arrival = button(arrivals, 'btn btn-sm btn-outline-info d-inline-flex align-items-center gap-1', label,
                null, () => actions.gmPushPartyToEncounter(point.inventory_id, row.encounter_id));
            arrival.dataset.ftEncounterArrivalId = point.inventory_id;
            arrival.replaceChildren();
            icon(arrival, point.item_type === 'map_marker' ? 'map_marker' : 'encounter_links', 16);
            arrival.append(node('span', '', label));
        }
        button(arrivals, `btn btn-sm ${points.length ? 'btn-outline-secondary' : 'btn-outline-info'}`, 'Default entry', null,
            () => actions.gmPushPartyToEncounter(row.encounter_id));
        travelCell.append(arrivals);
        body.append(tr, child);
    }
}

interface Peer { row: LinkRow; to?: LinkRow; from?: LinkRow }
function renderLinkPanel(output: HTMLElement, data: LinkPanelData): void {
    const message = (text: string): void => { output.append(node('p', 'text-muted small mb-0', text)); };
    if (!data.has_parent) { message('Set a campaign or adventure parent to manage encounter links.'); return; }
    const peers = new Map<string, Peer>();
    // Outbound peers came first in the old maps. Within either direction the final (oldest)
    // active duplicate supplies its privacy flag, because fetch_encounter_links sorted DESC.
    for (const row of [...data.links.filter(row => key(row.encounter_link_from) === key(data.encounter_id)),
        ...data.links.filter(row => key(row.encounter_link_to) === key(data.encounter_id))]) {
        const id = key(row.encounter_id);
        const peer = peers.get(id) ?? { row };
        if (key(row.encounter_link_from) === key(data.encounter_id)) peer.to = row;
        if (key(row.encounter_link_to) === key(data.encounter_id)) peer.from = row;
        peers.set(id, peer);
    }
    if (!peers.size) {
        if (!data.suppress_empty_peer_message) message('No links yet. Select encounters above; this list updates automatically.');
        return;
    }
    const readable = new Set(data.readable_encounter_ids.map(key)), locations = new Set(data.readable_location_ids.map(key));
    const rows = [...peers.values()].sort((a, b) => {
        const ar = readable.has(key(a.row.encounter_id)) ? a.row : null;
        const br = readable.has(key(b.row.encounter_id)) ? b.row : null;
        const za = ar?.encounter_zone_number, zb = br?.encounter_zone_number;
        const emptyA = za == null || za === '', emptyB = zb == null || zb === '';
        return (emptyA !== emptyB ? emptyA ? 1 : -1 : !emptyA ? Number(za) - Number(zb) : 0)
            // The previous comparator read location_zone_prefix at the root of a bucketed
            // encounter, where it was absent. Location and name therefore break zone ties.
            || compare(ar?.encounter_location_name, br?.encounter_location_name)
            || compare(ar?.encounter_name, br?.encounter_name);
    });
    const wrap = node('div', 'table-responsive'), table = node('table', 'table table-sm table-bordered align-middle mb-0');
    const head = node('thead', 'table-light'), headers = node('tr');
    for (const label of ['Connected Encounter', 'Direction', 'From them', 'Connection']) {
        const th = node('th', label === 'Connected Encounter' ? '' : 'text-center text-nowrap', label);
        th.scope = 'col';
        if (label !== 'Connected Encounter') th.style.width = '1%';
        if (label === 'Connected Encounter') {
            th.title = 'Links to other encounters (outbound) and from others (inbound). Private hides the connection from players without GM campaign access (secret room / passage).';
            icon(th, 'info');
        }
        if (label === 'From them') th.title = 'Remove the inbound leg: link from the connected encounter to the one you are editing.';
        headers.append(th);
    }
    head.append(headers);
    const body = node('tbody');
    table.append(head, body);
    wrap.append(table);
    output.append(wrap);
    const refresh = async (): Promise<void> => { await actions.refreshEncounterLinksSummary?.(); };
    for (const peer of rows) {
        const row = peer.row, id = row.encounter_id;
        const tr = node('tr');
        tr.dataset.ftEncounterLinkPeer = id;
        const label = link(cell(tr), url(`encounter.php?scene=edit&encounter_id=${encodeURIComponent(id)}`), '', '');
        if (!readable.has(key(id))) label.textContent = 'Unavailable encounter';
        else {
            icon(label, row.encounter_location_type || 'location');
            const names = [locations.has(key(row.encounter_location_parent_id)) ? plain(row.encounter_location_parent_name) : '',
                locations.has(key(row.encounter_location_id)) ? plain(row.encounter_location_name) : '', plain(row.encounter_name)].filter(Boolean);
            const z = zone(row);
            label.append(document.createTextNode(` ${z ? `${z} — ` : ''}${names.join(', ')}`));
        }
        cell(tr, 'text-center text-nowrap').append(node('span', `badge ${peer.to && peer.from ? 'bg-secondary' : peer.to ? 'bg-primary' : 'bg-info text-dark'}`,
            peer.to && peer.from ? 'Both' : peer.to ? 'To' : 'From'));
        const inbound = cell(tr, 'text-center text-nowrap');
        if (peer.from) {
            const buttons = node('div', 'd-inline-flex align-items-center justify-content-center');
            inbound.append(buttons);
            const help = 'Step 1: show remove. Step 2: confirm. Removes only the link from the connected encounter to the one you are editing (inbound "From" leg), not your link toward them.';
            const del = button(buttons, 'btn btn-warning btn-sm border', help, 'delete', () => {
                del.style.display = 'none'; confirm.style.display = 'inline-block';
            }, `inbound_link_delete_${id}`);
            const confirm = button(buttons, 'btn btn-danger btn-sm border', help, 'confirm', async () => {
                openSideLog();
                await queryData(url('options/tools/encounters/remove_inbound_encounter_link_inc.php'), 'div_sidelog_warning',
                    { encounter_id: data.encounter_id, linked_encounter_id: id, csrf_token: data.csrf_token });
                await refresh();
            }, `inbound_link_confirm_${id}`);
            confirm.style.display = 'none';
        }
        const isPrivate = Number(peer.to?.encounter_link_private ?? 0) !== 0 || Number(peer.from?.encounter_link_private ?? 0) !== 0;
        const privacy = button(cell(tr, 'text-center text-nowrap'), `btn btn-sm ${isPrivate ? 'btn-dark border' : 'btn-primary'}`,
            isPrivate ? 'Players without GM access cannot see this link.' : 'Visible to players on the map / encounter list.', isPrivate ? 'private' : 'public', async () => {
                // Same request / warning host / refresh as toggleEncounterLinkPrivate in the
                // scene host, with the island's session token. No form reinitialization.
                try {
                    const response = await fetch(url('options/tools/encounters/toggle_encounter_link_private_inc.php'), {
                        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'same-origin',
                        body: JSON.stringify({ encounter_id: data.encounter_id, linked_encounter_id: id, csrf_token: data.csrf_token }),
                    });
                    const html = await response.text();
                    const warning = document.getElementById('div_sidelog_warning');
                    if (warning) warning.innerHTML = html;
                    await refresh();
                } catch (error) { console.error(error); }
            });
        privacy.dataset.linkedEncounterId = id;
        privacy.append(document.createTextNode(isPrivate ? ' Private' : ' Public'));
        body.append(tr);
    }
}

const selector = '[data-ft-gm-encounter-panel], [data-ft-encounter-link-panel]';
export function mountEncounterPanelLists(root: ParentNode = document): void {
    const mounts = [...(root instanceof Element && root.matches(selector) ? [root] : []), ...Array.from(root.querySelectorAll(selector))];
    for (const mount of mounts) {
        if (!(mount instanceof HTMLElement) || mount.dataset.ftRawListMounted) continue;
        const script = mount.querySelector<HTMLScriptElement>(':scope > script[type="application/json"]');
        const output = mount.querySelector<HTMLElement>(':scope > [data-ft-raw-list-output]');
        if (!script?.textContent || !output) continue;
        mount.dataset.ftRawListMounted = '1';
        try {
            const data = JSON.parse(script.textContent) as GmPanelData | LinkPanelData;
            output.replaceChildren();
            if (data.schema === 'gm-encounter-panels-v1') renderGmPanel(output, data);
            else if (data.schema === 'encounter-link-panels-v1') renderLinkPanel(output, data);
            else throw new Error('encounter-panel-schema');
        } catch (error) {
            output.textContent = `Encounter list unavailable: ${error instanceof Error ? error.message : 'invalid-input'}`;
            output.classList.add('text-warning');
        }
    }
}
// Fragments enter via queryData and via the scene host's native refresh fetch. Inspect only added
// subtrees (never rescan the whole body on game mutations), including split parser insertions.
function start(): void {
    mountEncounterPanelLists();
    new MutationObserver(records => {
        for (const record of records) for (const added of record.addedNodes) {
            if (!(added instanceof Element)) continue;
            const mount = added.closest(selector);
            mountEncounterPanelLists(mount ?? added);
        }
    }).observe(document.body, { childList: true, subtree: true });
}
if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
